import { Immutable } from 'immer';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import { BadInputError } from '@watershed/errors/BadInputError';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import assertNever from '@watershed/shared-util/src/assertNever';

import { CanonicalSchemaDefinitionField } from '../canonicalSchemas/canonicalSchemaUtils';
import { ReferenceDataSchemaDefinition } from '../referenceData/ReferenceDataSchemaInterface';
import { parseUnitString } from '../unit';
import { ASTCall, ASTExpr } from './ast';
import { extractStringLiteral } from './literal';
import { parseExpression } from './parser';
import { RowExprType, RowType } from './row_types';
import { SqlExpression } from './types';

export function constructRowTyper(
  schemas: Map<string, ReferenceDataSchemaDefinition>,
  refTypes: Map<string, RowType>,
  refUniqueKeys: Map<string, Array<string>>
) {
  return (cols: Set<string>, slug: string): void => {
    const schema = schemas.get(slug);
    if (schema) {
      const rowType: RowType = new Map<string, RowExprType>();
      cols.forEach((col) => {
        const prop = schema.properties[col] as CanonicalSchemaDefinitionField;
        if (prop && prop.type) {
          let type: RowExprType | null = null;
          switch (prop.type) {
            case 'string':
              if (prop.format === 'date') {
                type = { ty: 'date' };
              } else if (prop.format === 'date-time') {
                type = { ty: 'datetime' };
              } else {
                type = { ty: 'string' };
              }
              break;
            case 'number':
            case 'integer':
              if (prop.unit) {
                type = {
                  ty: 'number',
                  unit: parseUnitString(prop.unit),
                };
              } else if (prop.unitColumn) {
                const unitProp = schema.properties[prop.unitColumn];
                if (unitProp && unitProp.enum) {
                  const encodedValuesToUnits = new Map(
                    unitProp.enum.map((enumValue) => [
                      enumValue,
                      parseUnitString(enumValue),
                    ])
                  );
                  type = {
                    ty: 'quantity',
                    units: Array.from(encodedValuesToUnits.values()),
                    encodedValuesToUnits,
                    column: col,
                  };
                } else {
                  type = { ty: 'number' };
                }
              } else {
                type = { ty: 'number' };
              }
              break;
            case 'boolean':
              type = { ty: 'boolean' };
              break;
            default:
              assertNever(prop.type);
          }

          if (!type) return;

          if (schema.required && schema.required.includes(col)) {
            rowType.set(col, type);
          } else {
            rowType.set(col, { ty: 'nullable', baseTy: type });
          }
        }
      });
      refTypes.set(slug, rowType);
      if (schema.uniqueKeys) {
        refUniqueKeys.set(slug, schema.uniqueKeys);
      }
    }
  };
}

/**
 * Recurse through an expression and accumulate names of called functions
 */
export function functionNamesInExpression(
  expr: ASTExpr,
  seenFunctionNames: Set<string> = new Set()
): Set<string> {
  // the call, unary, lookup, binary, and comparison expression types
  // contain sub-expressions; accumulate them for deeper checks
  const nextExprs: Array<ASTExpr> = [];
  if (expr.t === 'call') {
    seenFunctionNames.add(expr.name);
    nextExprs.push(
      ...expr.args,
      ...Array.from(expr.kwargs.values()).map(({ expr }) => expr)
    );
  } else if (expr.t === 'unary') {
    nextExprs.push(expr.expr);
  } else if (expr.t === 'binary' || expr.t === 'comparison') {
    nextExprs.push(expr.r, expr.l);
  } else if (
    expr.t === 'number' ||
    expr.t === 'string' ||
    expr.t === 'boolean' ||
    expr.t === 'identifier' ||
    expr.t === 'null'
  ) {
    // these have no sub-expressions
  } else {
    // guard against unchecked new expression types that may be added
    assertNever(expr);
  }

  nextExprs.forEach((x) => {
    functionNamesInExpression(x, seenFunctionNames).forEach((f) =>
      seenFunctionNames.add(f)
    );
  });

  return seenFunctionNames;
}

/**
 * Recurse through an expression and accumulate names of referenced identifiers
 */
export function identifierNamesInExpression(
  expr: ASTExpr,
  seenIdentifierNames: Set<string> = new Set()
): Set<string> {
  // the call, unary, lookup, binary, and comparison expression types
  // contain sub-expressions; accumulate them for deeper checks
  const nextExprs: Array<ASTExpr> = [];
  if (expr.t === 'identifier') {
    seenIdentifierNames.add(expr.name);
  } else if (expr.t === 'call') {
    nextExprs.push(
      ...expr.args,
      ...Array.from(expr.kwargs.values()).map(({ expr }) => expr)
    );
  } else if (expr.t === 'unary') {
    nextExprs.push(expr.expr);
  } else if (expr.t === 'binary' || expr.t === 'comparison') {
    nextExprs.push(expr.r, expr.l);
  } else if (
    expr.t === 'number' ||
    expr.t === 'string' ||
    expr.t === 'boolean' ||
    expr.t === 'null'
  ) {
    // these have no sub-expressions
  } else {
    // guard against unchecked new expression types that may be added
    assertNever(expr);
  }

  nextExprs.forEach((x) => {
    identifierNamesInExpression(x, seenIdentifierNames).forEach((f) =>
      seenIdentifierNames.add(f)
    );
  });

  return seenIdentifierNames;
}

// This is here because regular Math.max() will silently return NaN if any of the
// arguments are NaN or undefined. This is not what we want, but while lodash's
// max doesn't do this, we want to log something if we get NaNs so we can fix it.
export function safeMaxStage(
  rawArgs:
    | Immutable<Array<SqlExpression | number>>
    | IterableIterator<SqlExpression>,
  defaultStage?: number
): number {
  const args = Array.from(rawArgs);

  if (args.length === 0) {
    if (typeof defaultStage === 'number') {
      return defaultStage;
    }
    throw new UnexpectedError('safeMax got no arguments');
  }

  return Math.max(
    ...args.map((value: { stage: number } | number): number => {
      const stage = (typeof value === 'number' ? value : value.stage) as number;

      if (typeof stage !== 'number') {
        throw new UnexpectedError(`safeMax got non-number stage: ${stage}`, {
          data: { args },
        });
      }
      return stage;
    })
  );
}

export type SlugParts = {
  referenceDataName: string;
  referenceDataVersionName: string | null;
};

export type ResolvedSlugParts = {
  referenceDataName: string;
  referenceDataVersionName: string;
};

const slugRegex = /^(?:r!)?([0-9a-z_]+)(?::([0-9a-z_]+))?$/i;

export function parseSlug(slug: string): SlugParts {
  const m = slug.match(slugRegex);
  if (m == null) {
    throw new BadInputError(
      `Invalid slug: ${slug}. Expected a string like "ecoinvent_391_freight" or "defra_waste_2017_and_2018:initial_version"`
    );
  }
  return {
    referenceDataName: m[1],
    referenceDataVersionName: m[2] ?? null,
  };
}

export function assembleSlug(parts: SlugParts): string {
  if (parts.referenceDataVersionName) {
    return `${parts.referenceDataName}:${parts.referenceDataVersionName}`;
  } else {
    return parts.referenceDataName;
  }
}

/**
 * Recurse through an expression and extract all ref() and ref?() call nodes
 */
export function extractRefCallsFromExpression(
  expr: ASTExpr,
  refCalls: Array<ASTCall> = []
): Array<ASTCall> {
  // the call, unary, binary, and comparison expression types
  // contain sub-expressions; accumulate them for deeper checks
  const nextExprs: Array<ASTExpr> = [];

  switch (expr.t) {
    case 'call':
      // If this is a ref() or ref?() call, add it to our collection
      if (expr.name === 'ref' || expr.name === 'ref?') {
        refCalls.push(expr);
      }
      // Also check arguments for nested ref() calls
      nextExprs.push(
        ...expr.args,
        ...Array.from(expr.kwargs.values()).map(({ expr }) => expr)
      );
      break;
    case 'unary':
      nextExprs.push(expr.expr);
      break;
    case 'binary':
    case 'comparison':
      nextExprs.push(expr.r, expr.l);
      break;
    case 'number':
    case 'string':
    case 'boolean':
    case 'identifier':
    case 'null':
      // these have no sub-expressions
      break;
    default:
      // guard against unchecked new expression types that may be added
      assertNever(expr);
  }

  nextExprs.forEach((x) => {
    extractRefCallsFromExpression(x, refCalls);
  });

  return refCalls;
}

/**
 * Extracts the all unique reference data slug parts from ref() and ref?() in
 * the given expressions.
 */
export function extractRefDataSlugPartsFromExpressions({
  expressions,
}: {
  expressions: Array<string>;
}): Array<SlugParts> {
  // remove duplicate expressions
  const uniqueExpressions = uniq(expressions);

  // extract unique ref data slugs from the expressions
  const slugs = uniq(
    uniqueExpressions.flatMap((expression) => {
      // parse the expression into an AST and extract all ref() and ref?() calls
      const ast = parseExpression(expression);
      const refCalls = extractRefCallsFromExpression(ast);

      const uniqueRefCalls = uniqBy(refCalls, (refCall) => refCall.args[0]);
      return uniqueRefCalls.map((refCall) =>
        extractStringLiteral(refCall.args[0])
      );
    })
  );
  return slugs.map((slug) => parseSlug(slug));
}
